export function readAdminSessionUser() {
  try {
    const raw = localStorage.getItem('admin_user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const SESSION_IDENTITY_KEYS = ['nom', 'prenom', 'telephone', 'email', 'role'];

/**
 * Met à jour l'identité affichée dans la session Admin.
 * N'écrit jamais token ni mot de passe.
 */
export function patchAdminSessionIdentity(partial = {}) {
  const current = readAdminSessionUser() || {};
  const next = { ...current };
  for (const key of SESSION_IDENTITY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(partial, key)) {
      next[key] = partial[key];
    }
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('admin_user', JSON.stringify(next));
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('authUserUpdated'));
  }
  return next;
}

export function isValidLoginEmail(value) {
  const email = String(value || '').trim();
  const at = email.indexOf('@');
  return at > 0 && email.indexOf('.', at + 1) > at + 1;
}

/** Payload PUT /users/:id — identité personnelle, sans rôle ni password. */
export function buildOwnAccountUpdatePayload({ nom, prenom, telephone, email }) {
  return {
    nom: String(nom || '').trim(),
    prenom: String(prenom || '').trim(),
    telephone: String(telephone || '').trim(),
    email: String(email || '').trim().toLowerCase(),
  };
}

export function isOwnAccountUpdatePayloadSafe(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const keys = Object.keys(payload);
  const allowed = ['nom', 'prenom', 'telephone', 'email'];
  if (!keys.length || !keys.every((key) => allowed.includes(key))) return false;
  if (Object.prototype.hasOwnProperty.call(payload, 'role')) return false;
  if (Object.prototype.hasOwnProperty.call(payload, 'password')) return false;
  if (Object.prototype.hasOwnProperty.call(payload, 'token')) return false;
  return true;
}

export function formatRoleLabel(role, platformProfile = null) {
  if (role === 'SUPER_ADMIN' || role === 'super-admin') return 'Super Admin';
  if (role === 'ADMIN' || role === 'admin') return 'Admin';
  if (role === 'COMMERCANT' || role === 'commercant') return 'Commerçant';
  if (role === 'NR2J_STAFF') {
    if (platformProfile === 'COMMERCIAL_NR2J') return 'Commercial';
    if (platformProfile === 'MANAGER_NR2J') return 'Manager';
    if (platformProfile === 'ADMINISTRATIF_NR2J') return 'Administratif';
    return 'Collaborateur NR2J';
  }
  return role || '—';
}

export function getSessionPrenom(user) {
  return String(user?.prenom || '').trim();
}

export function getSessionNom(user) {
  return String(user?.nom || '').trim();
}

export function getSessionEmail(user) {
  return String(user?.email || '').trim();
}

/** Nom affiché header + Paramètres — même règle. */
export function getSessionDisplayName(user) {
  const full = `${getSessionPrenom(user)} ${getSessionNom(user)}`.trim();
  if (full) return full;
  const email = getSessionEmail(user);
  if (email) return email;
  return formatRoleLabel(user?.role);
}

export function getAuthenticatedIdentity(user) {
  return {
    displayName: getSessionDisplayName(user),
    roleLabel: formatRoleLabel(user?.role, user?.platformProfile),
    email: getSessionEmail(user),
    telephone: getSessionTelephone(user),
    initials: getSessionInitials(user),
  };
}

export function getSessionTelephone(user) {
  return String(user?.telephone || user?.phone || '').trim();
}

export function getSessionInitials(user) {
  const prenom = getSessionPrenom(user);
  const nom = getSessionNom(user);
  if (prenom && nom) return `${prenom[0]}${nom[0]}`.toUpperCase();
  if (prenom) return prenom.slice(0, 2).toUpperCase();
  if (nom) return nom.slice(0, 2).toUpperCase();
  const email = getSessionEmail(user);
  if (email) return email.slice(0, 2).toUpperCase();
  return 'CR';
}

export function formatEstablishmentAddress(commercant) {
  if (!commercant) return '';
  const line = String(commercant.rue || commercant.adresse || '').trim();
  const city = [commercant.codePostal, commercant.ville].filter(Boolean).join(' ').trim();
  const country = String(commercant.pays || '').trim();
  return [line, city, country].filter(Boolean).join(', ');
}
